import {
  anyPendingUpdated,
  applyDriveDataUpdates,
  OrchidDrivePendingData,
} from "../components/utils";
import {
  OrchidDrivePath,
  OrchidDriveFile,
  OrchidDriveDirectory,
  APIOrchidDriveFile,
  APIOrchidDriveDirectory,
} from "../types";

import { OrchidDriveData } from "../types";

type OrchidDriveFilesReducerResetAction = {
  type: "reset";
};

type OrchidDriveFilesReducerAddPendingAction = {
  type: "add_pending";
  files: APIOrchidDriveFile[];
  directories: APIOrchidDriveDirectory[];
};

type OrchidDriveFilesReducerUpdateAvailableAction = {
  type: "update_available";
  fileUpdates: OrchidDriveFile[];
  directoryUpdates: OrchidDriveDirectory[];
};

function getEntryPath(
  directoryId: string | null,
  directories: { [key: string]: OrchidDriveDirectory },
): OrchidDrivePath | undefined {
  const path: OrchidDrivePath = [];

  let currentDirectoryId = directoryId;
  while (currentDirectoryId !== null) {
    const currentDirectory = directories[currentDirectoryId];
    if (!currentDirectory) return undefined;
    path.unshift(currentDirectory.directoryId);
    currentDirectoryId = currentDirectory.parentDirectoryId;
  }

  return path;
}

function updatePaths(driveData: OrchidDriveData): OrchidDriveData {
  const newDriveData: OrchidDriveData = {
    files: { ...driveData.files },
    directories: { ...driveData.directories },
  };
  let changed = false;

  for (const fileEntry of Object.values(driveData.files)) {
    if (fileEntry.path === undefined) {
      const path = getEntryPath(
        fileEntry.parentDirectoryId,
        driveData.directories,
      );
      if (path !== undefined) {
        newDriveData.files[fileEntry.fileDataId] = { ...fileEntry, path };
        changed = true;
      }
    }
  }

  for (const directoryEntry of Object.values(driveData.directories)) {
    if (directoryEntry.path === undefined) {
      const path = getEntryPath(
        directoryEntry.parentDirectoryId,
        driveData.directories,
      );
      if (path !== undefined) {
        newDriveData.directories[directoryEntry.directoryId] = {
          ...directoryEntry,
          path,
        };
        changed = true;
      }
    }
  }

  return changed ? newDriveData : driveData;
}

function omitAvailable(
  pending: OrchidDrivePendingData,
  newlyAvailableEntryIds: Set<string>,
): OrchidDrivePendingData {
  if (
    !Object.keys(pending.files).some((entryId) =>
      newlyAvailableEntryIds.has(entryId),
    ) &&
    !Object.keys(pending.directories).some((entryId) =>
      newlyAvailableEntryIds.has(entryId),
    )
  ) {
    return pending;
  }
  const newPending = {
    files: {} as { [name: string]: APIOrchidDriveFile },
    directories: {} as { [name: string]: APIOrchidDriveDirectory },
  };

  for (const [entryId, entry] of Object.entries(pending.files)) {
    if (!newlyAvailableEntryIds.has(entryId)) {
      newPending.files[entryId] = entry;
    }
  }

  for (const [entryId, entry] of Object.entries(pending.directories)) {
    if (!newlyAvailableEntryIds.has(entryId)) {
      newPending.directories[entryId] = entry;
    }
  }

  return newPending;
}

export default function OrchidDriveFilesReducer(
  state: {
    pending: OrchidDrivePendingData;
    available: OrchidDriveData;
  },
  action:
    | OrchidDriveFilesReducerResetAction
    | OrchidDriveFilesReducerAddPendingAction
    | OrchidDriveFilesReducerUpdateAvailableAction,
) {
  switch (action.type) {
    case "reset": {
      if (
        Object.keys(state.pending.files).length === 0 &&
        Object.keys(state.pending.directories).length === 0 &&
        Object.keys(state.available.files).length === 0 &&
        Object.keys(state.available.directories).length === 0
      ) {
        return state;
      }
      return {
        pending: { files: {}, directories: {} },
        available: { files: {}, directories: {} },
      };
    }
    case "add_pending": {
      if (!anyPendingUpdated(action.files, action.directories, state.pending)) {
        return state;
      }
      const newPending = {
        files: { ...state.pending.files },
        directories: { ...state.pending.directories },
      };

      for (const update of action.files) {
        const existing = state.pending.files[update.file_data_id];
        if (
          !existing ||
          new Date(existing.modify_datetime).getTime() <
            new Date(update.modify_datetime).getTime()
        ) {
          newPending.files[update.file_data_id] = update;
        }
      }
      for (const update of action.directories) {
        const existing = state.pending.directories[update.directory_id];
        if (
          !existing ||
          new Date(existing.modify_datetime).getTime() <
            new Date(update.modify_datetime).getTime()
        ) {
          newPending.directories[update.directory_id] = update;
        }
      }

      return {
        ...state,
        pending: newPending,
      };
    }
    case "update_available": {
      const newAvailable = applyDriveDataUpdates(
        state.available,
        action.fileUpdates,
        action.directoryUpdates,
      );
      if (state.available === newAvailable) return state;

      const newlyAvailableEntryIds = new Set([
        ...Object.keys(action.fileUpdates),
        ...Object.keys(action.directoryUpdates),
      ]);
      return {
        pending: omitAvailable(state.pending, newlyAvailableEntryIds),
        available: updatePaths(newAvailable),
      };
    }
  }
  throw new Error(`unexpected action type: ${action}`);
}
